import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandLess";
import { Alert, Box, IconButton, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import HIGHLIGHT_ITEMS from "../../constants/highlightItems";
import { Translations } from "../../localization/translations";
import { ClassificationRecordsTreeSearchResponse } from "../../models/ClassificationRecordsTreeSearchResponse";
import { ClassifierStructureExistenceInfoResponse } from "../../models/ClassifierStructureExistenceInfoResponse";
import { TreeLayerRecordDto } from "../../models/TreeLayerRecordDto";
import { TreeLayerRecordsResponse } from "../../models/TreeLayerRecordsResponse";
import SearchHiglight from "../../utils/searchHighlight";
import EmptySearchResults from "../EmptySearchResults";
import KlasisLoader from "../KlasisLoader";
import ClassifierRecordActions from "./ClassifierRecordActions";
import ClassifierRecordDetails from "./ClassifierRecordDetails";
import {
    GetTreeRecords,
    SearchTreeRecords,
} from "./ClassifierRecordsHierarchicalViewData";

const treeItemColumnStyle = {
    display: "flex",
    flexDirection: "column",
};

const ulStyle = {
    listStyleType: "none",
};

interface IClassifierRecordsHierarchicalViewProps {
    classifierId: string;
    classifierLocale: string;
    level?: number;
    searchPhrase?: string;
    dateFilterValue: Dayjs | undefined | null;
    structuresExistenceInfo: ClassifierStructureExistenceInfoResponse;
    getTreeRecordsDateFilter: () => [
        Date | undefined,
        Date | undefined,
        Date | undefined
    ];
}

const ClassifierRecordsHierarchicalView: React.FC<
    IClassifierRecordsHierarchicalViewProps
> = (props: IClassifierRecordsHierarchicalViewProps) => {
    const [treeItems, setTreeItems] = useState<TreeLayerRecordDto[]>([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [hasMoreRecordsToShow, setHasMoreRecordsToShow] = useState(false);

    const { t } = useTranslation();

    useEffect(() => {
        if (props.searchPhrase) searchData();
        else refreshData();
    }, [
        props.level,
        props.searchPhrase,
        props.dateFilterValue,
        props.classifierLocale,
        props.structuresExistenceInfo,
    ]);
    const searchData = (): void => {
        const [onDate, validFromDate, validToDate] =
            props.getTreeRecordsDateFilter();

        if (!onDate && !validFromDate && !validToDate) {
            setTreeItems([]);
            setHasMoreRecordsToShow(false);
            setIsLoadingData(false);
            return;
        }

        setIsLoadingData(true);

        SearchTreeRecords({
            classifierId: props.classifierId,
            treeRecordsLevel: props.level,
            searchPhrase: props.searchPhrase,
            onDate: onDate,
            validFromDate: validFromDate,
            validToDate: validToDate,
            classifierLocale: props.classifierLocale,
            onSuccess: (data: ClassificationRecordsTreeSearchResponse) => {
                setTreeItems(data.items);
                setHasMoreRecordsToShow(data.hasMore);
            },
            onFinally: () => {
                setIsLoadingData(false);
            },
        });
    };
    const refreshData = (): void => {
        const [onDate, validFromDate, validToDate] =
            props.getTreeRecordsDateFilter();

        if (!onDate && !validFromDate && !validToDate) {
            setTreeItems([]);
            setHasMoreRecordsToShow(false);
            setIsLoadingData(false);
            return;
        }

        setIsLoadingData(true);

        GetTreeRecords({
            classifierId: props.classifierId,
            treeRecordsLevel: props.level,
            onDate: onDate,
            validFromDate: validFromDate,
            validToDate: validToDate,
            classifierLocale: props.classifierLocale,
            onSuccess: (data: TreeLayerRecordsResponse) => {
                setTreeItems(data.items);
                setHasMoreRecordsToShow(false);
            },
            onFinally: () => {
                setIsLoadingData(false);
            },
        });
    };

    const handleExpandStateChange = (
        shouldBeExpanded: boolean,
        item: TreeLayerRecordDto
    ): void => {
        if (shouldBeExpanded) {
            const [onDate, validFromDate, validToDate] =
                props.getTreeRecordsDateFilter();

            GetTreeRecords({
                classifierId: props.classifierId,
                classifierLocale: props.classifierLocale,
                parentRecordId: item.id,
                treeRecordsLevel: props.level,
                onDate: onDate,
                validFromDate: validFromDate,
                validToDate: validToDate,
                onSuccess: (data: TreeLayerRecordsResponse) => {
                    item.children = data.items;
                    setTreeItems([...treeItems]);
                },
            });
        } else {
            item.children = [];
            setTreeItems([...treeItems]);
        }
    };

    if (!isLoadingData && !treeItems.length) {
        return (
            <EmptySearchResults
                title={t(Translations.classifier.label.noClassificationRecords)}
                description={t(
                    Translations.classifier.label
                        .noClassificationRecordsDescription
                )}
            />
        );
    }

    return (
        <>
            {hasMoreRecordsToShow ? (
                <Alert
                    variant="outlined"
                    severity="warning"
                    sx={{ marginTop: "1rem", marginBottom: "1rem" }}
                >
                    {t(
                        Translations.classifier.mainData
                            .insufficientSearchPhrase
                    )}
                </Alert>
            ) : null}

            {isLoadingData ? (
                <KlasisLoader />
            ) : (
                <ul style={{ ...ulStyle, padding: 0 }}>
                    {treeItems.map((item) => (
                        <ClassifierRecordsHierarchicalViewItem
                            classifierId={props.classifierId}
                            classifierLocale={props.classifierLocale}
                            data={item}
                            key={`tree-item-${item.id}`}
                            onExpandStateChange={handleExpandStateChange}
                        >
                            {item.name}
                        </ClassifierRecordsHierarchicalViewItem>
                    ))}
                </ul>
            )}
        </>
    );
};

interface IClassifierRecordsHierarchicalViewItemProps {
    data: TreeLayerRecordDto;
    classifierId: string;
    classifierLocale: string;
    onExpandStateChange: (
        shouldBeExpanded: boolean,
        item: TreeLayerRecordDto
    ) => void;
}

const ClassifierRecordsHierarchicalViewItem: React.FC<
    IClassifierRecordsHierarchicalViewItemProps
> = (props: IClassifierRecordsHierarchicalViewItemProps) => {
    const [isDataExpanded, setIsDataExpanded] = useState(false);

    const isExpanded = (): boolean =>
        props.data.hasChildren && props.data.children.length > 0;

    const handleExpandButtonClick = (): void => {
        const shouldBeExpanded = !isExpanded();

        props.onExpandStateChange(shouldBeExpanded, props.data);
    };

    const getTitleText = (): JSX.Element | string | undefined => {
        if (!props.data.searchData?.highlights?.length) return props.data.name;

        const titleHighlight = props.data.searchData.highlights.find(
            (highlight) => highlight.fieldName === HIGHLIGHT_ITEMS.title
        );

        if (!titleHighlight || !titleHighlight.fragments.length)
            return props.data.name;

        return SearchHiglight.parse(titleHighlight.fragments[0]);
    };

    const renderExtraFieldSearchHiglights = ():
        | JSX.Element
        | JSX.Element[]
        | null => {
        if (!props.data.searchData?.highlights?.length) return null;

        const fieldHighlights = props.data.searchData.highlights.filter(
            (highlight) =>
                highlight.fieldName === HIGHLIGHT_ITEMS.fieldContent &&
                highlight.fragments.length
        );

        if (!fieldHighlights.length) return null;

        return fieldHighlights.map((highlight, index) => (
            <Box key={`fragment-${index}`} sx={{ marginLeft: "1rem" }}>
                <Typography variant="caption">
                    {SearchHiglight.parse(highlight.fragments[0])}
                </Typography>
            </Box>
        ));
    };

    const renderExpandButton = (): JSX.Element | null => {
        if (!props.data.hasChildren) return null;

        return (
            <IconButton color="primary" onClick={handleExpandButtonClick}>
                {isExpanded() ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
        );
    };

    const renderCode = (): JSX.Element => {
        return (
            <Typography
                paddingLeft={props.data.hasChildren ? "initial" : "1.5rem"}
                color="text.secondary"
                sx={{ lineHeight: "2.5rem" }}
            >
                <b>{props.data.code}</b>
            </Typography>
        );
    };

    const renderTitle = (): JSX.Element => {
        return (
            <Typography
                color="primary"
                display="inline-block"
                lineHeight="2.5rem"
                marginLeft="1rem"
            >
                {getTitleText()}
            </Typography>
        );
    };

    return (
        <li>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                }}
            >
                <Box sx={treeItemColumnStyle}>{renderExpandButton()}</Box>
                <Box sx={treeItemColumnStyle}>{renderCode()}</Box>
                <Box sx={{ ...treeItemColumnStyle, flex: 1 }}>
                    {renderTitle()}
                    {renderExtraFieldSearchHiglights()}
                    {isDataExpanded && (
                        <Box sx={{ marginLeft: "1rem" }}>
                            <ClassifierRecordDetails
                                classifierId={props.classifierId}
                                recordId={props.data.id}
                            />
                        </Box>
                    )}
                </Box>
                <Box sx={treeItemColumnStyle}>
                    <Box>
                        <ClassifierRecordActions
                            classifierId={props.classifierId}
                            classifierRecordCode={props.data.code}
                            isDataExpanded={isDataExpanded}
                            localeCode={props.classifierLocale}
                            onExpandedStateChange={(
                                newExpandedState: boolean
                            ) => setIsDataExpanded(newExpandedState)}
                        />
                    </Box>
                </Box>
            </Box>

            {props.data.children?.length ? (
                <ul style={ulStyle}>
                    {props.data.children.map((item) => (
                        <ClassifierRecordsHierarchicalViewItem
                            classifierId={props.classifierId}
                            classifierLocale={props.classifierLocale}
                            data={item}
                            key={`tree-item-${item.id}`}
                            onExpandStateChange={props.onExpandStateChange}
                        />
                    ))}
                </ul>
            ) : null}
        </li>
    );
};

export default ClassifierRecordsHierarchicalView;
